import { z } from 'zod';

/**
 * User response schema.
 */
export const roleSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  isSystem: z.boolean(),
});

export type RoleSummary = z.infer<typeof roleSummarySchema>;

export const userResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  roleIds: z.array(z.string().uuid()),
  roles: z.array(roleSummarySchema).nullable().optional(),
  rowStatus: z.number().int(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export type UserResponse = z.infer<typeof userResponseSchema>;

/**
 * Create user request schema.
 */
export const createUserSchema = z.object({
  email: z.string().email('Invalid email format'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roleIds: z.array(z.string().uuid()).min(1, 'At least one role is required'),
});

export type CreateUserRequest = z.infer<typeof createUserSchema>;

/**
 * Update user request schema.
 */
export const updateUserSchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  phone: z
    .string()
    .regex(/^[+\d][\d\s()\-\.]{6,20}$/, 'Invalid phone number format')
    .optional(),
  roleIds: z.array(z.string().uuid()).min(1, 'At least one role is required').optional(),
});

export type UpdateUserRequest = z.infer<typeof updateUserSchema>;

/**
 * User permissions response schema.
 * Resolved permissions for the current user (UX-only, backend enforces).
 */
export const userPermissionsResponseSchema = z.object({
  permissions: z.array(z.string()),
  scopes: z.record(z.string(), z.string()),
  isSystemAdmin: z.boolean(),
});

export type UserPermissionsResponse = z.infer<typeof userPermissionsResponseSchema>;
